// Sugalarm — Cycle 354: sensor-pause hold helpers (pure; vitest-pinned).
//
// A hold means the household DECLARED the member's CGM feed expectedly
// dark ("changing the sensor" / "away"), so the cycle-353 blackout pages
// stand down until `until`. Bounded by construction (the server clamps
// durations — sensor_change ≤3h, away ≤24h; there is no permanent hold)
// and it gates ONLY the blackout push rail — glucose alarms never
// consult it.

import type { SensorPause } from "./types";

/** True iff the hold exists and hasn't expired. Clients must always gate
 *  display on this (an expired hold may linger in settings until the
 *  server lazily clears it). */
export function isPauseActive(
  pause: SensorPause | null | undefined,
  nowMs: number,
): boolean {
  if (!pause || !pause.until) return false;
  const until = Date.parse(pause.until);
  return Number.isFinite(until) && until > nowMs;
}

/** Identity comparison for a hold (cycle 375).
 *
 *  `until` is a server-minted, microsecond-precision instant, so together
 *  with `reason` it identifies one specific hold. Used to tell "the hold I
 *  just cleared, still lingering in a stale snapshot" apart from "a
 *  DIFFERENT hold somebody else just declared" — a distinction the UI must
 *  get right, because masking the second one would claim monitoring is
 *  armed while the server is suppressing it. */
export function isSamePause(
  a: SensorPause | null | undefined,
  b: SensorPause | null | undefined,
): boolean {
  if (a == null || b == null) return a == null && b == null;
  return a.until === b.until && a.reason === b.reason;
}

export function pauseReasonLabel(reason: SensorPause["reason"]): string {
  return reason === "sensor_change" ? "Sensor change" : "Away";
}

export function pauseReasonEmoji(reason: SensorPause["reason"]): string {
  return reason === "sensor_change" ? "🔄" : "🏖️";
}

/** The away-duration choices offered by the UI (server clamps to ≤24h). */
export const AWAY_DURATION_CHOICES: { label: string; minutes: number }[] = [
  { label: "4 hours", minutes: 240 },
  { label: "12 hours", minutes: 720 },
  { label: "24 hours", minutes: 1440 },
];

/** "1:30 PM" / "13:30" — when the hold ends, in local time. */
export function pauseUntilLabel(pause: SensorPause, h24: boolean): string {
  const d = new Date(pause.until);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], {
    hour: h24 ? "2-digit" : "numeric",
    minute: "2-digit",
    hour12: !h24,
  });
}

// ─── Cycle 375: No-Data sequence copy ──────────────────────────────────
//
// Every household-facing string in the sequence lives here as a named
// constant so the operator can tweak wording WITHOUT touching JSX. The
// server never sends this copy — it only ever sends `reason` + `until`.

/** Ask affordance — the question the strip opens with (mockup, cycle 375.1). */
export const ASK_EYEBROW = "No sensor data";
export function askPrompt(firstName: string): string {
  return `Is ${firstName} changing the sensor, or away from the kiosk?`;
}

/** The away-duration step gets its own eyebrow + prompt (mockup). */
export const AWAY_EYEBROW = "Pause while away";
export const AWAY_PROMPT = "Pause alerts for how long?";

/** Held/confirmation eyebrow — the state the household is now in. */
export const PAUSE_HELD_EYEBROW = "Alerts paused";

/** The reassurance line under BOTH held headlines. This is the sentence
 *  that makes a hold feel safe rather than like a switch someone has to
 *  remember to flip back: the server auto-clears the hold the moment
 *  readings return (see `sensor_blackout_service.sweep`). */
export const PAUSE_HELD_REASSURANCE =
  "If data is detected before then, monitoring will resume automatically.";

/** "Away — Alerts are paused until 1:30 AM." Reason-aware by design: the
 *  household must be able to tell at a glance WHICH declaration is
 *  standing, because the two carry very different expectations (a sensor
 *  warmup is minutes, a trip is hours). */
export function pauseHeldHeadline(
  reason: SensorPause["reason"],
  untilLabel: string,
): string {
  const p = pauseHeldHeadlineParts(reason, untilLabel);
  return `${p.lead}${p.until}${p.tail}`;
}

/** The same headline split so the resume TIME can be emphasised in its own
 *  span (the mockup colours it amber + tabular-nums — it is the one datum
 *  the household actually needs off this line). Concatenating the parts
 *  must always equal `pauseHeldHeadline`; a vitest case pins that. */
export function pauseHeldHeadlineParts(
  reason: SensorPause["reason"],
  untilLabel: string,
): { lead: string; until: string; tail: string } {
  return {
    lead: `${pauseReasonLabel(reason)} — Alerts are paused until `,
    until: untilLabel,
    tail: ".",
  };
}

// ── Cycle 375.1: the quiet "resumed, feed is live" state ───────────────
//
// The mockup only ever depicts the DARK-feed strip, so it has no answer
// for the Settings declare-ahead card, where the feed is live by
// definition. Falling back to the ask-buttons there re-asks the question
// the household just answered — the very loop this sequence removes — and
// the watching copy ("Still no data detected.") would be flatly false with
// readings arriving. So resuming on a live feed lands here instead:
// actionless, calm, and accurate. Operator-approved 2026-08-03.
export const RESUMED_EYEBROW = "Alerts active";
export const RESUMED_HEADLINE = "Monitoring is on — nothing is paused.";

/** Watching-for-data eyebrow — armed and waiting, NOT still paused. */
export const WATCHING_EYEBROW = "Watching for data";
export const WATCHING_HEADLINE = "Still no data detected.";
export const WATCHING_BODY =
  "Sugar Alarm will resume when sensor data is detected.";
